import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

interface StatEntry {
  value: number | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface DashboardStatsState {
  dataupBalance: StatEntry;
  seagmBalance: StatEntry;
  totalUsers: StatEntry;
  totalTransactions: StatEntry;
}

const makeInitialStat = (): StatEntry => ({
  value: null,
  loading: false,
  error: null,
  lastUpdated: null,
});

const initialState: DashboardStatsState = {
  dataupBalance: makeInitialStat(),
  seagmBalance: makeInitialStat(),
  totalUsers: makeInitialStat(),
  totalTransactions: makeInitialStat(),
};

const extractNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const resolveNumeric = (payload: unknown, fallbackText: string, candidateKeys: string[]): number | null => {
  const direct = extractNumeric(payload);
  if (direct !== null) return direct;

  if (payload && typeof payload === "object") {
    for (const key of candidateKeys) {
      const candidate = (payload as Record<string, unknown>)[key];
      const resolved = extractNumeric(candidate);
      if (resolved !== null) return resolved;
    }
    const dataField = (payload as Record<string, unknown>)["data"];
    if (dataField && typeof dataField === "object") {
      return resolveNumeric(dataField, fallbackText, candidateKeys);
    }
  }

  return extractNumeric(fallbackText);
};

const parseResponse = async (response: Response) => {
  const textPayload = await response.text();
  let parsedPayload: unknown = textPayload;
  try {
    parsedPayload = textPayload ? JSON.parse(textPayload) : null;
  } catch {
    parsedPayload = textPayload;
  }
  return { parsedPayload, textPayload };
};

export const fetchDataupBalance = createAsyncThunk<
  number | null,
  void,
  { rejectValue: string; state: RootState }
>("dashboardStats/fetchDataupBalance", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/dashboard/dataup-balance`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error('Failed to fetch DataUp balance');
    }
    return resolveNumeric(parsedPayload, textPayload, ["wallet_balance"]);
  } catch (err: any) {
    return rejectWithValue(err.response?.message ?? "Failed to fetch DataUp balance");
  }
});

export const fetchSeagmBalance = createAsyncThunk<
  number | null,
  void,
  { rejectValue: string; state: RootState }
>("dashboardStats/fetchSeagmBalance", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/dashboard/seagm-balance`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error('Failed to fetch DataUp balance');
    }

    return resolveNumeric(parsedPayload, textPayload, ["wallet_balance", "balance", "amount", "value"]);
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch SEAGM balance");
  }
});

export const fetchTotalUsers = createAsyncThunk<
  number | null,
  void,
  { rejectValue: string; state: RootState }
>("dashboardStats/fetchTotalUsers", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/dashboard/users-count`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error("Failed to fetch total users");
    }

    return resolveNumeric(parsedPayload, textPayload, ["users_count", "total", "count", "value"]);
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch total users");
  }
});

export const fetchTotalTransactions = createAsyncThunk<
  number | null,
  void,
  { rejectValue: string; state: RootState }
>("dashboardStats/fetchTotalTransactions", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/dashboard/transactions-count`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error("Failed to fetch total transactions");
    }

    return resolveNumeric(parsedPayload, textPayload, ["transactions_count", "total", "count", "value"]);
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch total transactions");
  }
});

const dashboardStatSlice = createSlice({
  name: "dashboardStats",
  initialState,
  reducers: {
    setDataupBalance(state, action: PayloadAction<number | null>) {
      state.dataupBalance.value = action.payload;
      state.dataupBalance.lastUpdated = new Date().toISOString();
      state.dataupBalance.error = null;
    },
    clearDataupBalance(state) {
      state.dataupBalance = makeInitialStat();
    },
    setSeagmBalance(state, action: PayloadAction<number | null>) {
      state.seagmBalance.value = action.payload;
      state.seagmBalance.lastUpdated = new Date().toISOString();
      state.seagmBalance.error = null;
    },
    clearSeagmBalance(state) {
      state.seagmBalance = makeInitialStat();
    },
    setTotalUsers(state, action: PayloadAction<number | null>) {
      state.totalUsers.value = action.payload;
      state.totalUsers.lastUpdated = new Date().toISOString();
      state.totalUsers.error = null;
    },
    clearTotalUsers(state) {
      state.totalUsers = makeInitialStat();
    },
    setTotalTransactions(state, action: PayloadAction<number | null>) {
      state.totalTransactions.value = action.payload;
      state.totalTransactions.lastUpdated = new Date().toISOString();
      state.totalTransactions.error = null;
    },
    clearTotalTransactions(state) {
      state.totalTransactions = makeInitialStat();
    },
    resetDashboardStats(state) {
      state.dataupBalance = makeInitialStat();
      state.seagmBalance = makeInitialStat();
      state.totalUsers = makeInitialStat();
      state.totalTransactions = makeInitialStat();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataupBalance.pending, (state) => {
        state.dataupBalance.loading = true;
        state.dataupBalance.error = null;
      })
      .addCase(fetchDataupBalance.fulfilled, (state, action) => {
        state.dataupBalance.loading = false;
        state.dataupBalance.value = action.payload ?? null;
        state.dataupBalance.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDataupBalance.rejected, (state, action) => {
        state.dataupBalance.loading = false;
        state.dataupBalance.error = action.payload ?? "Failed to fetch DataUp balance";
      })

      .addCase(fetchSeagmBalance.pending, (state) => {
        state.seagmBalance.loading = true;
        state.seagmBalance.error = null;
      })
      .addCase(fetchSeagmBalance.fulfilled, (state, action) => {
        state.seagmBalance.loading = false;
        state.seagmBalance.value = action.payload ?? null;
        state.seagmBalance.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSeagmBalance.rejected, (state, action) => {
        state.seagmBalance.loading = false;
        state.seagmBalance.error = action.payload ?? "Failed to fetch SEAGM balance";
      })

      .addCase(fetchTotalUsers.pending, (state) => {
        state.totalUsers.loading = true;
        state.totalUsers.error = null;
      })
      .addCase(fetchTotalUsers.fulfilled, (state, action) => {
        state.totalUsers.loading = false;
        state.totalUsers.value = action.payload ?? null;
        state.totalUsers.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchTotalUsers.rejected, (state, action) => {
        state.totalUsers.loading = false;
        state.totalUsers.error = action.payload ?? "Failed to fetch total users";
      })

      .addCase(fetchTotalTransactions.pending, (state) => {
        state.totalTransactions.loading = true;
        state.totalTransactions.error = null;
      })
      .addCase(fetchTotalTransactions.fulfilled, (state, action) => {
        state.totalTransactions.loading = false;
        state.totalTransactions.value = action.payload ?? null;
        state.totalTransactions.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchTotalTransactions.rejected, (state, action) => {
        state.totalTransactions.loading = false;
        state.totalTransactions.error = action.payload ?? "Failed to fetch total transactions";
      });
  },
});

export const {
  setDataupBalance,
  clearDataupBalance,
  setSeagmBalance,
  clearSeagmBalance,
  setTotalUsers,
  clearTotalUsers,
  setTotalTransactions,
  clearTotalTransactions,
  resetDashboardStats,
} = dashboardStatSlice.actions;

export const selectDashboardDataupBalance = (state: RootState) => state.dashboardStats.dataupBalance.value;
export const selectDashboardDataupBalanceLoading = (state: RootState) => state.dashboardStats.dataupBalance.loading;
export const selectDashboardDataupBalanceError = (state: RootState) => state.dashboardStats.dataupBalance.error;
export const selectDashboardDataupBalanceLastUpdated = (state: RootState) =>
  state.dashboardStats.dataupBalance.lastUpdated;

export const selectDashboardSeagmBalance = (state: RootState) => state.dashboardStats.seagmBalance.value;
export const selectDashboardSeagmBalanceLoading = (state: RootState) => state.dashboardStats.seagmBalance.loading;
export const selectDashboardSeagmBalanceError = (state: RootState) => state.dashboardStats.seagmBalance.error;
export const selectDashboardSeagmBalanceLastUpdated = (state: RootState) =>
  state.dashboardStats.seagmBalance.lastUpdated;

export const selectDashboardTotalUsers = (state: RootState) => state.dashboardStats.totalUsers.value;
export const selectDashboardTotalUsersLoading = (state: RootState) => state.dashboardStats.totalUsers.loading;
export const selectDashboardTotalUsersError = (state: RootState) => state.dashboardStats.totalUsers.error;
export const selectDashboardTotalUsersLastUpdated = (state: RootState) =>
  state.dashboardStats.totalUsers.lastUpdated;

export const selectDashboardTotalTransactions = (state: RootState) => state.dashboardStats.totalTransactions.value;
export const selectDashboardTotalTransactionsLoading = (state: RootState) =>
  state.dashboardStats.totalTransactions.loading;
export const selectDashboardTotalTransactionsError = (state: RootState) =>
  state.dashboardStats.totalTransactions.error;
export const selectDashboardTotalTransactionsLastUpdated = (state: RootState) =>
  state.dashboardStats.totalTransactions.lastUpdated;

export default dashboardStatSlice.reducer;
